import FlexibleContentTile from '../../../Shared/Tiles/FlexibleContentTile';
import RecentActivitiesNoData, { GetNoDataTile, INoDataTileMessage } from '../../../Shared/Tiles/NoDataTile';
import { useEffect, useState } from 'react';
import { Table, DataTable, TableCell, TableRow, TableHeader, TableBody } from '@lmig/lmds-react-table';
import './Policies.scss';
import { Pagination } from '@lmig/lmds-react-pagination';
import { Link } from '@lmig/lmds-react-link';
import { getPLPSTLink, getRecentActivitiesLink } from 'Foundation/Configurations/GlobalConfiguration';
import {
  IconLandlord,
  IconHome,
  IconAuto,
  IconMotorcycle,
  IconLandlordCondo,
  IconEarthquake,
  IconWatercraft,
  IconUmbrella,
  IconRenters,
  IconCondo,
  IconInfo,
  IconFilter,
} from '@lmig/lmds-react-icons';
import { GridRow, GridCol } from '@lmig/lmds-react-grid';
import { Checkbox, CheckboxGroup } from '@lmig/lmds-react-checkbox';
import { useLocalTabDataHandler, APIList } from '../../../Data/useLocalTabDataHandler';
import { Card } from '@mui/material';
import { toTitleCase } from 'Utilities/StringFormatter';
import { Tooltip } from '@lmig/lmds-react-tooltip';
import { StringHelper } from 'Utilities/StringHelper';

interface IPolicyTableData {
  customer: string;
  customerLastName: string;
  effective: string;
  policyNumber: string;
  product: string;
  transactionType: string;
}

enum TransactionType {
  NewBusiness = 'New Business',
  PolicyChange = 'Policy Change',
  Cancellation = 'Cancellation',
  Reinstatement = 'Reinstatement',
  NonRenewal = 'Non-Renewal',
}

interface IPolicyDocumentCustomer {
  insuredName: string;
  firstName: string;
  lastName: string;
}

interface IPolicyDocument {
  customers: IPolicyDocumentCustomer[];
  termPolicyEffectiveDate: string;
  policyNumber: string;
  product: string;
  transactionType: string;
  totalRenewalPremium: number;
  previousTermTotalPremium: number;
  percentChangeInPremium: string;
}

enum SortDirection {
  ascending = 'ascending',
  descending = 'descending',
  none = 'none',
}

enum PolicyTableSortableKeys {
  customer = 'customer',
  customerLastName = 'customerLastName',
  transactionType = 'transactionType',
  effective = 'effective',
}

interface SortDirections {
  [PolicyTableSortableKeys.customer]: SortDirection;
  [PolicyTableSortableKeys.customerLastName]: SortDirection;
  [PolicyTableSortableKeys.transactionType]: SortDirection;
  [PolicyTableSortableKeys.effective]: SortDirection;
}

export default function Policies(props: { activeTab: number }) {
  const [sortDirections, setSortDirections] = useState<SortDirections>({
    [PolicyTableSortableKeys.customer]: SortDirection.none,
    [PolicyTableSortableKeys.customerLastName]: SortDirection.none,
    [PolicyTableSortableKeys.transactionType]: SortDirection.none,
    [PolicyTableSortableKeys.effective]: SortDirection.descending, // set this to the opposite of what you initially want
  });
  const [initialDataLoad, setInitialDataLoad] = useState<IPolicyTableData[]>([]);
  const [initialLength, setInitialLength] = useState<number>(0);
  const [filteredTableData, setFilteredTableData] = useState<IPolicyTableData[]>([]);
  const [filteredPoliciesDisplay, setFilteredPoliciesDisplay] = useState<IPolicyTableData[]>([]);
  const [page, setPage] = useState(0);

  const [counts, setCounts] = useState<Array<[TransactionType, number | undefined]>>([
    [TransactionType.NewBusiness, undefined],
    [TransactionType.PolicyChange, undefined],
    [TransactionType.Cancellation, undefined],
    [TransactionType.Reinstatement, undefined],
    [TransactionType.NonRenewal, undefined],
  ]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const recentActivitiesLink = getRecentActivitiesLink();

  const [noDataTile, setNoDataTile] = useState<INoDataTileMessage>({
    leftText: '<div>Loading...</div>',
    rightText: '<div>Loading...</div>',
  });

  const [filtersSubjectCol, setSubjectColFilters] = useState<Array<TransactionType>>([]);
  const [rerender, setRerender] = useState(0);
  const [currentSortKey, setCurrentSortKey] = useState<PolicyTableSortableKeys | undefined>(
    PolicyTableSortableKeys.effective,
  );
  const [currentSortDirection, setCurrentSortDirection] = useState<SortDirection>(SortDirection.none);
  const { data, error, isLoading } = useLocalTabDataHandler({
    api: APIList.policies,
    body: JSON.stringify({
      policyTypes: (filtersSubjectCol && filtersSubjectCol.length > 0
        ? filtersSubjectCol
        : Object.values(TransactionType)
      ).map((x) => x.toLowerCase()),
      sortColumn: currentSortKey,
      SortDirection: currentSortDirection,
    }),
  });

  useEffect(() => {
    if (counts && filtersSubjectCol.length) {
      let total: number = 0;
      for (var i = 0; i < filtersSubjectCol.length; i++) {
        let pair: [TransactionType, number | undefined] | undefined = counts.find(
          (pair) => pair[0] == filtersSubjectCol[i],
        );
        let filteredCount: number = pair && pair[1] ? pair[1] : 0;
        total += filteredCount;
      }
      setTotalRecords(total);
    }
  }, [filtersSubjectCol, counts]);

  useEffect(() => {
    const getCustomerInsuredName = (customer: IPolicyDocumentCustomer) => {
      if (StringHelper.hasNonEmptyString(customer.insuredName)) {
        return customer.insuredName;
      } else if (StringHelper.hasNonEmptyString(customer.firstName) || StringHelper.hasNonEmptyString(customer.lastName)) {
        return `${customer.firstName} ${customer.lastName}`.trim();
      } else {
        return 'Not Available';
      }
    };

    const getCustomerLastName = (customer: IPolicyDocumentCustomer) => {
      if (StringHelper.hasNonEmptyString(customer.lastName)) {
        return customer.lastName;
      } else {
        return getCustomerInsuredName(customer);
      }
    }
    
    const formatJsonData = (policy: IPolicyDocument) =>
      ({
        customer: policy?.customers?.length ? toTitleCase(getCustomerInsuredName(policy.customers[0])) : 'Not Available',
        customerLastName: policy?.customers?.length ? toTitleCase(getCustomerLastName(policy.customers[0])) : 'Not Available',
        effective: policy.termPolicyEffectiveDate,
        policyNumber: policy?.policyNumber,
        product: policy?.product,
        transactionType: policy?.transactionType,
      }) as IPolicyTableData;

    const policyDataToTableData = (policies: IPolicyDocument[]) => policies.map(formatJsonData) as IPolicyTableData[];

    const isValidPolicy = (policy: IPolicyDocument) => policy?.transactionType.toUpperCase() !== 'RENEWAL';

    const handlePolicyData = () =>
      new Promise((resolve, reject) => {
        if (isLoading) resolve('loading');
        if (error) reject('error fetching data');
        if (!data?.length) reject('data is empty');

        let json = data?.policyDocumentList;
        json = json.filter(isValidPolicy);

        if (!json?.length) reject('no valid policies to display');

        const mappedData = policyDataToTableData(json);
        setInitialDataLoad(mappedData);
        setCounts([
          [TransactionType.NewBusiness, data?.newBusinessDocumentCount ?? 0],
          [TransactionType.PolicyChange, data?.policyChangeDocumentCount ?? 0],
          [TransactionType.Cancellation, data?.cancellationDocumentCount ?? 0],
          [TransactionType.Reinstatement, data?.reinstatementDocumentCount ?? 0],
          [TransactionType.NonRenewal, data?.nonrenewalDocumentCount ?? 0],
        ]);
        setTotalRecords(
          (data?.newBusinessDocumentCount ?? 0) +
            (data?.policyChangeDocumentCount ?? 0) +
            (data?.cancellationDocumentCount ?? 0) +
            (data?.reinstatementDocumentCount ?? 0) +
            (data?.nonrenewalDocumentCount ?? 0),
        );
        resolve(data);
      });

    handlePolicyData().catch(() => {
      GetNoDataTile('policies', setNoDataTile);
    });
  }, [data, error, isLoading]);

  const policyTableSorter = (key: PolicyTableSortableKeys) => {
    const getNone = {
      customer: SortDirection.none,
      customerLastName: SortDirection.none,
      transactionType: SortDirection.none,
      effective: SortDirection.none,
    };
    setSortDirections((prev) => ({
      ...getNone,
      [key]: prev[key] === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending,
    }));
    setCurrentSortKey(key);
    setCurrentSortDirection(sortDirections[key]);
  };

  useEffect(() => {
    const isValueForKeyNotNull = (key: string) => sortDirections[key as PolicyTableSortableKeys] !== SortDirection.none;
    const key = Object.keys(sortDirections).find(isValueForKeyNotNull) as PolicyTableSortableKeys;

    const switchValues = (a: Array<any>) => a.slice().reverse();

    const compareValues = (valuesToCompare: Array<any>, type: string) => {
      if (type === 'number') return Number(valuesToCompare[0]) - Number(valuesToCompare[1]);
      else if (type === 'string') return valuesToCompare[0].localeCompare(valuesToCompare[1]);
      else {
        return null;
      }
    };

    if (filteredTableData.length === 0 && filtersSubjectCol.length === 0) {
      setFilteredTableData(initialDataLoad);
    }

    if (filtersSubjectCol.length === 0 && filteredTableData.length !== 0) {
      setInitialLength(filteredTableData.length);
    }

    setFilteredTableData(() => {
      const finalData = filteredTableData.sort((a: IPolicyTableData, b: IPolicyTableData) => {
        let valuesToCompare = [a[key], b[key]];

        if (key === PolicyTableSortableKeys.customerLastName) {
          const aValue = !a[key] ? a[PolicyTableSortableKeys.customer] : a[key];
          const bValue = !b[key] ? b[PolicyTableSortableKeys.customer] : b[key];
          
          valuesToCompare = [aValue, bValue];
        }

        if (sortDirections[key] === SortDirection.descending) valuesToCompare = switchValues(valuesToCompare);
        if (key === PolicyTableSortableKeys.effective) valuesToCompare = switchValues(valuesToCompare);

        return compareValues(valuesToCompare, 'string');
      });
      return finalData;
    });

    setPage(0);
    setRerender((r) => r + 1);
  }, [sortDirections, initialDataLoad, filteredTableData]);

  useEffect(() => {
    if (page === undefined) {
      setPage(0);
    }
    const start = page * 5;
    setFilteredPoliciesDisplay(filteredTableData.slice(start, Math.min(start + 5, filteredTableData?.length)));
  }, [page, rerender]);

  const LMDSPagination = () => {
    const itemCount = filteredTableData?.length;

    const changeHandler = (e: any) => {
      setPage(e.page);
    };

    return (
      (itemCount > 5 && (
        <Pagination
          id="policies-pagination"
          className="recent-activities-pagination"
          withWords={false}
          itemCount={itemCount}
          onChange={changeHandler}
          showItemsPerPage={false}
          page={page}
          itemsPerPage={5}
        />
      )) || <></>
    );
  };

  const GetPolicyIcon = ({ policy }: { [key: string]: IPolicyTableData }) => {
    const iconMap: { [key: string]: any } = {
      home: <IconHome size="16" />,
      renters: <IconRenters size="16" />,
      condo: <IconCondo size="16" />,
      landlord: <IconLandlord size="16" />,
      'landlord condo': <IconLandlordCondo size="16" />,
      eq: <IconEarthquake size="16" />,
      auto: <IconAuto size="16" />,
      motorcycle: <IconMotorcycle size="16" />,
      umbrella: <IconUmbrella size="16" />,
      watercraft: <IconWatercraft size="16" />,
    };
    return iconMap[policy.product.toLowerCase()] ? iconMap[policy.product.toLowerCase()] : <></>;
  };

  const formatEffectiveDate = (date: string) => `${date.slice(5, 7)}/${date.slice(8)}/${date.slice(0, 4)}`;

  const toggleSubjectFilter: any = (value: TransactionType) => {
    if (filtersSubjectCol.includes(value))
      setSubjectColFilters(filtersSubjectCol.filter((filterVal: any) => filterVal !== value));
    else setSubjectColFilters([...filtersSubjectCol, value]);
  };

  useEffect(() => {
    setFilteredTableData(initialDataLoad);
  }, [filtersSubjectCol, initialDataLoad]);

  const FilterCard: any = ({ list = [], filterFunction = ([]) => {}, activeFilters = '' }) => {
    const handleChange = (e: any) => {
      const { value } = e.target;
      filterFunction(value);
    };

    return (
      <Card className="filter-tile">
        <GridRow>
          <div className="filter-title">
            <GridCol base={8}>
              <IconFilter size="16" /> <span className="transaction-type">Transaction type</span>
            </GridCol>
          </div>
        </GridRow>
        <div className="filter-menu">
          <CheckboxGroup onChange={handleChange}>
            {list.map(([value, count]: [string, number]) => {
              return (
                <Checkbox value={value} labelVisual={`${value} (${count})`} checked={activeFilters.includes(value)} />
              );
            })}
          </CheckboxGroup>
        </div>
      </Card>
    );
  };

  const Spinner = () => (
    <div className="spinner-container">
      <div className="spinner">
        <span className="spinner__sr">Loading…</span>
      </div>
    </div>
  );
  return isLoading ? (
    <Spinner />
  ) : initialLength !== 0 ? (
    <FlexibleContentTile
      data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
      tileClass="policies-tile"
      children={[
        <>
          <div id="policies-table-container" className="recent-activities-table-container">
            <div key={props.activeTab} className="policies-tab-content tab-content">
              <div className="policy-transactions-graphic">
                <div className="filterCard">
                  <FilterCard
                    activeFilters={filtersSubjectCol}
                    list={counts}
                    filterFunction={toggleSubjectFilter}
                    setSubjectColFilters={setSubjectColFilters}
                  />
                </div>
              </div>
              <div className="design-system-data-table-container">
                {initialLength !== 0 ? (
                  <div className="design-system-data-table">
                    <DataTable>
                      <Table className="design-system-table" spacing="micro">
                        <TableHeader id="policies-table-header">
                          <TableRow>
                            <TableCell
                              type="colHead"
                              isSortable
                              sortDirection={sortDirections.customerLastName}
                              onClick={() => policyTableSorter(PolicyTableSortableKeys.customerLastName)}
                            >
                              Customer
                            </TableCell>
                            <TableCell
                              type="colHead"
                              isSortable
                              sortDirection={sortDirections.transactionType}
                              onClick={() => policyTableSorter(PolicyTableSortableKeys.transactionType)}
                              className="small-column-header"
                            >
                              Transaction
                            </TableCell>
                            <TableCell
                              type="colHead"
                              isSortable
                              sortDirection={sortDirections.effective}
                              onClick={() => policyTableSorter(PolicyTableSortableKeys.effective)}
                              className="small-column-header"
                            >
                              Effective
                            </TableCell>
                          </TableRow>
                        </TableHeader>
                        <TableBody id="policies-table-body" key={rerender}>
                          {filteredPoliciesDisplay.length ? (
                            filteredPoliciesDisplay.map((data: IPolicyTableData, rowIndex: any) => (
                              <TableRow key={`${rowIndex}-policies`}>
                                <TableCell key={`${rowIndex}-policies-customer-name`}>
                                  <div className="customer-name-container policies-customer-name">
                                    <div className="policy-icon">
                                      <GetPolicyIcon policy={data} />
                                    </div>
                                    <Tooltip
                                      className="recent-activities-tile-tooltip"
                                      content={
                                        <Link target="_blank" href={getPLPSTLink() + '?Policy=' + data.policyNumber}>
                                          {data.customer}
                                        </Link>
                                      }
                                    >
                                      <div className="customer-name">
                                        <Link target="_blank" href={getPLPSTLink() + '?Policy=' + data.policyNumber}>
                                          {data.customer}
                                        </Link>
                                      </div>
                                    </Tooltip>
                                  </div>
                                </TableCell>
                                <TableCell
                                  key={`${rowIndex}-policies-transaction-type`}
                                  className="policies-table-transaction"
                                >
                                  <div>{data.transactionType}</div>
                                </TableCell>
                                <TableCell
                                  key={`${rowIndex}-policies-effective-date`}
                                  className="policies-table-effective-date"
                                >
                                  <div>{formatEffectiveDate(data.effective)}</div>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <></>
                          )}
                        </TableBody>
                      </Table>
                    </DataTable>

                    {filteredPoliciesDisplay.length === 0 ? (
                      <div className="no-data-found">
                        <div className="no-data-found-inner"></div>
                        No customers found. Try another filter
                      </div>
                    ) : (
                      <></>
                    )}

                    {filteredTableData && filteredTableData.length < totalRecords && page === 9 ? (
                      <div className="recent-activities-info-view-all">
                        <div className="recent-activities-info">
                          <IconInfo size="16" color="informative" />
                          Viewing only 50 most recent activities.
                          {recentActivitiesLink ? (
                            <Link
                              target="_blank"
                              href={recentActivitiesLink}
                              variant="standalone"
                              className="portal-tile-v2-header-link"
                            >
                              View all activity
                            </Link>
                          ) : (
                            <></>
                          )}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                ) : (
                  <Spinner />
                )}
              </div>
            </div>
            <LMDSPagination />
          </div>
        </>,
      ]}
    ></FlexibleContentTile>
  ) : (
    <RecentActivitiesNoData
      messageLeft={noDataTile.leftText}
      messageRight={noDataTile.rightText}
    ></RecentActivitiesNoData>
  );
}
